import type { dealerSearchOutgoingInternalError } from "@dealer-center/ddc-pages-account-search-interface";
import { isDealerSearchOutgoingInternalError } from "@dealer-center/ddc-pages-account-search-interface";
import type {
  colorInterface,
  featuresInterface,
  featuresOptionsInterface,
  optionsInterface,
  specificationsInterface,
  vehicleInterface
} from "@dealer-center/ddc-pages-vin-decode-interface";
import { useQuery } from "@tanstack/react-query";

const handleParsedJson = <TItem>(
  newData?: TItem | dealerSearchOutgoingInternalError
) => {
  if (isDealerSearchOutgoingInternalError(newData)) {
    throw new Error(newData.message);
  }
  return newData;
};
const handleResponse = <TItem>(response: Response) => {
  if (!response.ok) {
    throw new Error(response.statusText);
  } else {
    return response.json() as Promise<
      TItem | dealerSearchOutgoingInternalError
    >;
  }
};

const basePath = "/landing/nxlambda/vin-decode/";
const getAutoDataVehicleUrl = (vin: string, country?: "US" | "CA") =>
  `${basePath}vehicle/${vin}` + (country ? `?country=${country}` : "");
const getAutoDataOptionsUrl = (vehicleId: string) =>
  `${basePath}options/${vehicleId}`;
const getAutoDataColorsExteriorUrl = (vehicleId: string) =>
  `${basePath}colors-exterior/${vehicleId}`;
const getAutoDataColorsInteriorUrl = (vehicleId: string) =>
  `${basePath}colors-interior/${vehicleId}`;
const getAutoDataSpecificationsUrl = (vehicleId: string) =>
  `${basePath}specifications/${vehicleId}`;
const getAutoDataFeatureOptionsUrl = (vehicleId: string) =>
  `${basePath}feature-options/${vehicleId}`;
const getAutoDataFeaturesUrl = (vehicleId: string) =>
  `${basePath}features/${vehicleId}`;

const getAutoDataVehicle = async (vin: string, country?: "US" | "CA") => {
  const url = getAutoDataVehicleUrl(vin, country);
  const result = await fetch(url);
  const response = await handleResponse<vehicleInterface[]>(result);
  return handleParsedJson<vehicleInterface[]>(response);
};
const getAutoDataOptions = async (vehicleId: string) => {
  const url = getAutoDataOptionsUrl(vehicleId);
  const result = await fetch(url);
  const response = await handleResponse<optionsInterface[]>(result);
  return handleParsedJson<optionsInterface[]>(response);
};

const getAutoDataColorsExterior = async (vehicleId: string) => {
  const url = getAutoDataColorsExteriorUrl(vehicleId);
  const result = await fetch(url);
  const response = await handleResponse<colorInterface[]>(result);
  return handleParsedJson<colorInterface[]>(response);
};

const getAutoDataColorsInterior = async (vehicleId: string) => {
  const url = getAutoDataColorsInteriorUrl(vehicleId);
  const result = await fetch(url);
  const response = await handleResponse<colorInterface[]>(result);
  return handleParsedJson<colorInterface[]>(response);
};

const getAutoDataSpecifications = async (vehicleId: string) => {
  const url = getAutoDataSpecificationsUrl(vehicleId);
  const result = await fetch(url);
  const response = await handleResponse<specificationsInterface[]>(result);
  return handleParsedJson<specificationsInterface[]>(response);
};

const getAutoDataFeatureOptions = async (vehicleId: string) => {
  const url = getAutoDataFeatureOptionsUrl(vehicleId);
  const result = await fetch(url);
  const response = await handleResponse<featuresOptionsInterface[]>(result);
  return handleParsedJson<featuresOptionsInterface[]>(response);
};

const getAutoDataFeatures = async (vehicleId: string) => {
  const url = getAutoDataFeaturesUrl(vehicleId);
  const result = await fetch(url);
  const response = await handleResponse<featuresInterface[]>(result);
  return handleParsedJson<featuresInterface[]>(response);
};

export interface userSearchFetchInterface {
  name: string;
}
const queryKeyBase = "ddc-pages-vin-decode";

export function useGetAutoDataVehicle(vin?: string, country?: "US" | "CA") {
  return useQuery({
    queryKey: [queryKeyBase + "-vehicle", { vin, country }],
    queryFn: () => getAutoDataVehicle(vin || "", country),
    enabled: !!vin && !!country
  });
}

export function useGetAutoDataOptions(vehicleId: string) {
  return useQuery({
    queryKey: [queryKeyBase + "-options", vehicleId],
    queryFn: () => getAutoDataOptions(vehicleId),
    enabled: !!vehicleId
  });
}

export function useGetAutoDataColorsExterior(vehicleId: string) {
  return useQuery({
    queryKey: [queryKeyBase + "-colors-exterior", vehicleId],
    queryFn: () => getAutoDataColorsExterior(vehicleId),
    enabled: !!vehicleId
  });
}

export function useGetAutoDataColorsInterior(vehicleId: string) {
  return useQuery({
    queryKey: [queryKeyBase + "-colors-interior", vehicleId],
    queryFn: () => getAutoDataColorsInterior(vehicleId),
    enabled: !!vehicleId
  });
}

export function useGetAutoDataSpecifications(vehicleId: string) {
  return useQuery({
    queryKey: [queryKeyBase + "-specifications", vehicleId],
    queryFn: () => getAutoDataSpecifications(vehicleId),
    enabled: !!vehicleId
  });
}

export function useGetAutoDataFeatureOptions(vehicleId: string) {
  return useQuery({
    queryKey: [queryKeyBase + "-feature-options", vehicleId],
    queryFn: () => getAutoDataFeatureOptions(vehicleId),
    enabled: !!vehicleId
  });
}

export function useGetAutoDataFeatures(vehicleId: string) {
  return useQuery({
    queryKey: [queryKeyBase + "-features", vehicleId],
    queryFn: () => getAutoDataFeatures(vehicleId),
    enabled: !!vehicleId
  });
}
